import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import Icon from '../components/Icon';

export const ONBOARDING_KEY = 'onboarding_seen';

interface Slide { icon: string; key: string; }

const SLIDES: Slide[] = [
  { icon: 'magic', key: 'agentFree' },
  { icon: 'wallet', key: 'oneWallet' },
  { icon: 'shield-alt', key: 'secure' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { enterGuest } = useAuth();
  const [index, setIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const browse = () => {
    try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch { /* ignore */ }
    enterGuest();
    navigate('/browse');
  };

  useEffect(() => {
    try {
      if (localStorage.getItem(ONBOARDING_KEY) === '1') navigate('/login', { replace: true });
    } catch {
      /* ignore */
    }
  }, [navigate]);

  const finish = (path: string) => {
    try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch { /* ignore */ }
    navigate(path);
  };

  const handleNext = () => {
    if (!scrollRef.current) return;
    if (index < SLIDES.length - 1) {
      const w = scrollRef.current.clientWidth;
      scrollRef.current.scrollTo({ left: w * (index + 1), behavior: 'smooth' });
    } else {
      finish('/register');
    }
  };

  const onScroll = () => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.clientWidth;
    const next = Math.round(scrollRef.current.scrollLeft / w);
    if (next !== index) setIndex(next);
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: colors.bg,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38, height: 38, borderRadius: 19, backgroundColor: '#000',
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}
          >
            <img src="/icons/icon-512.png" alt="" style={{ width: 30, height: 30, objectFit: 'contain' }} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: 1, color: colors.text }}>AGA</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => finish('/login')}
            style={{
              minWidth: 44, minHeight: 44, padding: '0 4px',
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 700, color: colors.textSub,
            }}
          >
            {t('auth.onboarding.skip')}
          </button>
          <button
            onClick={() => finish('/login')}
            style={{
              minHeight: 44, padding: '0 14px',
              borderRadius: 999, border: `1px solid ${colors.cardBorder}`,
              backgroundColor: colors.card, cursor: 'pointer',
              fontSize: 14, fontWeight: 800, color: colors.accentText,
            }}
          >
            {t('auth.onboarding.login')}
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          flex: 1,
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
        }}
      >
        {SLIDES.map((slide, i) => (
          <div
            key={i}
            style={{
              flex: '0 0 100%',
              width: '100%',
              scrollSnapAlign: 'start',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 32px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 140, height: 140, borderRadius: 70,
                backgroundColor: colors.accentSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 36,
              }}
            >
              <Icon name={slide.icon} size={56} color={colors.accentText} />
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: colors.text, marginBottom: 14 }}>
              {t(`auth.onboarding.slides.${slide.key}.title`)}
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.5, color: colors.textSub }}>
              {t(`auth.onboarding.slides.${slide.key}.body`)}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 24 }}>
        {SLIDES.map((_, i) => (
          <div
            key={i}
            style={{
              height: 8,
              width: i === index ? 22 : 8,
              borderRadius: 4,
              backgroundColor: i === index ? colors.accent : colors.cardBorder,
              transition: 'width 200ms ease, background-color 200ms ease',
            }}
          />
        ))}
      </div>

      <div style={{ padding: '0 20px calc(16px + env(safe-area-inset-bottom, 0px))' }}>
        <Button
          label={index === SLIDES.length - 1 ? t('auth.onboarding.getStarted') : t('auth.onboarding.next')}
          icon="arrow-right"
          iconRight
          onClick={handleNext}
        />
        <Button
          label={t('auth.onboarding.login')}
          variant="ghost"
          icon="sign-in-alt"
          onClick={() => finish('/login')}
          style={{ marginTop: 8 }}
        />
        <Button
          label={t('auth.login.browseAsGuest')}
          variant="ghost"
          icon="eye"
          onClick={browse}
          style={{ marginTop: 4 }}
        />
      </div>
    </div>
  );
}
